import { PrismaEnums } from '@airreading/schema/browser';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { dayOfWeekSchema } from './meeting-block.js';
import { InstantInterval } from '../temporal-utils/instant-interval.js';
import { fullClassTimeSchema } from './class-time.js';
import { orgWithSchoolsEdlinkIdentifiableSchema } from './org.js';
import { teachingPreferenceSchema } from './coach-shared.js';

export const coachAvailabilityTypeSchema = z.nativeEnum(PrismaEnums.CoachAvailabilityType);
export type CoachAvailabilityType = z.infer<typeof coachAvailabilityTypeSchema>;
export const CoachAvailabilityType = coachAvailabilityTypeSchema.enum;


export const minimalAvailabilitySchema = z.object({
  day: dayOfWeekSchema,
  startPlainTime: z.instanceof(Temporal.PlainTime),
  endPlainTime: z.instanceof(Temporal.PlainTime),
});
export type MinimalAvailability = z.infer<typeof minimalAvailabilitySchema>;

export const coachAvailabilitySchema = z
  .object({
    id: z.string(),
    userId: z.string(),
    updatedAt: z.instanceof(Temporal.Instant),
  })
  .extend(minimalAvailabilitySchema.shape)
  .extend({
    type: coachAvailabilityTypeSchema,
  });
export type CoachAvailability = z.infer<typeof coachAvailabilitySchema>;

export const coachAvailabilityPlainTimesSchema = z.object({
  startPlainTime: z.instanceof(Temporal.PlainTime),
  endPlainTime: z.instanceof(Temporal.PlainTime),
});
export type CoachAvailabilityPlainTimes = z.infer<typeof coachAvailabilityPlainTimesSchema>;

export const coachAvailabilityTimeOptionsSchema = z
  .object({
    timeZone: z.string(),
  })
  .extend(minimalAvailabilitySchema.shape)
  .extend({
    type: coachAvailabilityTypeSchema.default('regular'),
  });
export type CoachAvailabilityTimeOptions = z.infer<typeof coachAvailabilityTimeOptionsSchema>;

export const createCoachAvailabilityOptionsSchema = z.object({
  userId: z.string(),
  day: dayOfWeekSchema,
  startPlainTime: z.string(),
  endPlainTime: z.string(),
  type: coachAvailabilityTypeSchema.default('regular'),
});
export type CreateCoachAvailabilityOptions = z.infer<typeof createCoachAvailabilityOptionsSchema>;

export const updateCoachAvailabilitiesOptionsSchema = z.array(coachAvailabilityTimeOptionsSchema);
export type UpdateCoachAvailabilitiesOptions = z.infer<typeof updateCoachAvailabilitiesOptionsSchema>;

export type TeachingDay = z.infer<typeof dayOfWeekSchema>;
export const teachingDays: TeachingDay[] = dayOfWeekSchema.options;

export const classEligibilitySchema = z.enum([
  'can_teach_intervention',
  'can_teach_practice',
  'can_teach_diagnostic',
  'air_crew',
  'imported_from_v1',
]);
export type ClassEligibility = z.infer<typeof classEligibilitySchema>;
export const ClassEligibility = classEligibilitySchema.enum;

export const coachOverallGradeRankOrder = ['S', 'A', 'B', 'C', 'D', 'Never_Taught', 'Not_reviewed'] as const;
export const coachOverallGradeEnum = z.enum(coachOverallGradeRankOrder);

export const availableCoachesFilterSchema = z.object({
  org: orgWithSchoolsEdlinkIdentifiableSchema,
  eligibleGrades: z.array(coachOverallGradeEnum),
  requiredClassEligibilities: z.array(classEligibilitySchema),
  teachingPreferences: z.array(teachingPreferenceSchema),
});
export type AvailableCoachesFilter = z.infer<typeof availableCoachesFilterSchema>;

export const sessionWithTimeSchema = z.object({
  id: z.string(),
  coachId: z.string(),
  startInstant: z.instanceof(Temporal.Instant),
  endInstant: z.instanceof(Temporal.Instant),
});
export type SessionWithTime = z.infer<typeof sessionWithTimeSchema>;

export const sessionAvailabilitySchema = sessionWithTimeSchema.pick({
  coachId: true,
  startInstant: true,
  endInstant: true,
});
export type SessionAvailability = z.infer<typeof sessionAvailabilitySchema>;

export const availableCoachesScheduleSchema = z.object({
  startPlainDate: z.instanceof(Temporal.PlainDate),
  endPlainDate: z.instanceof(Temporal.PlainDate),
  classTime: z.lazy(() => fullClassTimeSchema),
  timeZone: z.string(),
  coachFilters: availableCoachesFilterSchema.optional(),
});
export type AvailableCoachesSchedule = z.infer<typeof availableCoachesScheduleSchema>;

export const availabilityToDateTimeSchema = z.object({
  startWithTimeZone: z.instanceof(Temporal.ZonedDateTime),
  endWithTimeZone: z.instanceof(Temporal.ZonedDateTime),
  startAdjustedPlainDate: z.instanceof(Temporal.PlainDate),
  endAdjustedPlainDate: z.instanceof(Temporal.PlainDate),
  startAdjustedPlainTime: z.instanceof(Temporal.PlainTime),
  endAdjustedPlainTime: z.instanceof(Temporal.PlainTime),
  adjustedDay: dayOfWeekSchema,
});
export type AvailabilityToDateTime = z.infer<typeof availabilityToDateTimeSchema>;

export const timeZoneAdjustmentSchema = z.object({
  sourceTimeZone: z.string(),
  targetTimeZone: z.string(),
});
export type TimeZoneAdjustment = z.infer<typeof timeZoneAdjustmentSchema>;

export const coachAvailabilityFromFilterSchema = z.object({
  classTimes: z.union([z.lazy(() => z.array(fullClassTimeSchema)), z.array(z.string())]),
  orgId: z.string(),
  classEligibilities: z.array(classEligibilitySchema).optional(),
  overrideStartPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  overrideEndPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  shouldAvoidConflicts: z.boolean().optional(),
});
export type CoachAvailabilityFromFilter = z.infer<typeof coachAvailabilityFromFilterSchema>;

export const coachAssignmentStateSchema = z.object({
  coachId: z.string(),
  overallGrade: coachOverallGradeEnum,
  numAssignedClasses: z.number().int().min(0),
  assignedIntervals: z.array(z.instanceof(InstantInterval)),
  hasGoogleMeetLink: z.boolean().optional(),
});
export type CoachAssignmentState = z.infer<typeof coachAssignmentStateSchema>;

export const eligibleCoachesForClassTimeSchema = z.object({
  classTime: z.lazy(() => fullClassTimeSchema),
  sessionsInRange: z.array(sessionAvailabilitySchema),
  coachFilters: availableCoachesFilterSchema,
  overrideStartPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  overrideEndPlainDate: z.instanceof(Temporal.PlainDate).optional(),
});
export type EligibleCoachesForClassTime = z.infer<typeof eligibleCoachesForClassTimeSchema>;
